import { api } from './api';
import { downloadBlob } from './download';

export type AdminMerchantRow = {
  userId: string;
  businessName: string;
  contactEmail: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'suspended';
  ownerEmail: string;
  ownerName: string | null;
  createdAt: string;
};

export type AdminMerchantDetail = {
  merchant: {
    id: string;
    userId: string;
    businessName: string;
    contactNumber: string | null;
    contactEmail: string | null;
    businessAddress: Record<string, unknown>;
    pickupAddress: Record<string, unknown>;
    pickupRegionCode: string | null;
    platformFeePct: string | null;
    showFeeToMerchant: boolean;
    status: string;
    rejectionReason: string | null;
    approvedAt: string | null;
    ownerEmail: string;
    ownerName: string | null;
  };
  submissions: Array<{
    id: string;
    requirementKey: string;
    fileKey: string | null;
    textValue: string | null;
    status: 'pending' | 'approved' | 'rejected';
    rejectionReason: string | null;
  }>;
};

export type Paged<T> = { rows: T[]; total: number; page: number; pageSize: number };

export async function listMerchants(params: {
  status?: string;
  page?: number;
  pageSize?: number;
  search?: string;
}) {
  return (await api.get<Paged<AdminMerchantRow>>('/admin/merchants', { params })).data;
}
export async function getMerchant(userId: string) {
  return (await api.get<AdminMerchantDetail>(`/admin/merchants/${userId}`)).data;
}
export type CreateMerchantArgs = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  businessName: string;
  contactNumber: string;
  contactEmail: string;
  pickupRegionCode?: string;
  /** Full FF delivery address (PSGC + delivery contact + full-address text). */
  pickupAddress: import('@mlm/shared').Address;
  /** Per-merchant platform commission % (clamped to the tenant band server-side). */
  platformFeePct?: string;
  /** Whether the merchant sees their earnings preview on the product form. */
  showFeeToMerchant?: boolean;
};
export async function createMerchant(args: CreateMerchantArgs) {
  return (await api.post<{ userId: string; status: string }>('/admin/merchants', args)).data;
}
export async function updateMerchantCommission(
  userId: string,
  args: { platformFeePct?: string; showFeeToMerchant?: boolean },
) {
  return (await api.patch<{ userId: string }>(`/admin/merchants/${userId}`, args)).data;
}
export async function approveMerchant(
  userId: string,
  args: { platformFeePct: string; showFeeToMerchant?: boolean },
) {
  return (await api.post(`/admin/merchants/${userId}/approve`, args)).data;
}
export async function rejectMerchant(userId: string, reason?: string) {
  return (await api.post(`/admin/merchants/${userId}/reject`, { reason })).data;
}
export async function suspendMerchant(userId: string, reason?: string) {
  return (await api.post(`/admin/merchants/${userId}/suspend`, { reason })).data;
}
export async function reactivateMerchant(userId: string) {
  return (await api.post(`/admin/merchants/${userId}/reactivate`)).data;
}
export async function reviewRequirement(
  submissionId: string,
  decision: 'approved' | 'rejected',
  rejectionReason?: string,
) {
  return (
    await api.post(`/admin/merchant-requirements/${submissionId}/review`, {
      decision,
      rejectionReason,
    })
  ).data;
}
export async function requirementFileUrl(submissionId: string) {
  return (await api.get<{ url: string }>(`/admin/merchant-requirements/${submissionId}/file-url`))
    .data;
}

/** Realtime Unilevel read-back fields (shared by product + variant rows). */
export type RtuRowFields = {
  rtuCeilingPct: string | null;
  rtuLevels: number | null;
  rtuLevelShares: string[] | null;
};
export type AdminMerchantVariantRow = RtuRowFields & {
  id: string;
  sku: string;
  name: string;
  attributes: Record<string, string>;
  price: string;
  srp: string | null;
  stockQty: number | null;
  platformFeePct: string | null;
  pvCommissionPct: string | null;
  vatEnabled: boolean | null;
  vatPct: string | null;
  active: boolean;
};
export type AdminMerchantProductRow = RtuRowFields & {
  id: string;
  sku: string;
  name: string;
  price: string;
  approvalStatus: 'pending' | 'approved' | 'rejected';
  platformFeePct: string | null;
  pvCommissionPct: string | null;
  vatEnabled: boolean;
  vatPct: string;
  businessName: string;
  variants: AdminMerchantVariantRow[];
  createdAt: string;
};
/** Realtime Unilevel approve fields (nullable = clear; shared product + variant). */
export type RtuApproveFields = {
  rtuCeilingPct?: string | null;
  rtuLevels?: number | null;
  rtuLevelShares?: string[] | null;
};
export type ApproveMerchantProductVariantOverride = RtuApproveFields & {
  variantId: string;
  platformFeePct?: string | null;
  pvCommissionPct?: string | null;
  vatEnabled?: boolean | null;
  vatPct?: string | null;
};
export type ApproveMerchantProductArgs = RtuApproveFields & {
  platformFeePct?: string;
  pvCommissionPct?: string;
  vatEnabled?: boolean;
  vatPct?: string;
  variantOverrides?: ApproveMerchantProductVariantOverride[];
};
export async function listMerchantProducts(params: {
  status?: string;
  page?: number;
  pageSize?: number;
}) {
  return (await api.get<Paged<AdminMerchantProductRow>>('/admin/merchant-products', { params }))
    .data;
}
export async function approveMerchantProduct(id: string, args: ApproveMerchantProductArgs = {}) {
  return (await api.post(`/admin/merchant-products/${id}/approve`, args)).data;
}
export async function rejectMerchantProduct(id: string, rejectionReason?: string) {
  return (await api.post(`/admin/merchant-products/${id}/reject`, { rejectionReason })).data;
}

export type AdminMerchantPayoutRow = {
  id: string;
  merchantUserId: string;
  businessName: string;
  ownerEmail: string;
  amount: string;
  netAmount: string;
  method: string;
  status: string;
  payload: Record<string, unknown>;
  requestedAt: string;
};
export async function listMerchantPayouts(params: {
  status?: string;
  page?: number;
  pageSize?: number;
}) {
  return (await api.get<Paged<AdminMerchantPayoutRow>>('/admin/merchant-payouts', { params })).data;
}
export async function markMerchantPayoutPaid(id: string) {
  return (await api.post(`/admin/merchant-payouts/${id}/mark-paid`)).data;
}
export async function rejectMerchantPayout(id: string, reason?: string) {
  return (await api.post(`/admin/merchant-payouts/${id}/reject`, { reason })).data;
}

export type AdminMerchantPayoutBatch = {
  id: string;
  periodStart: string;
  periodEnd: string;
  total: string;
  currency: string;
  status: string;
  createdAt: string;
  processedAt: string | null;
};
export async function createMerchantPayoutBatch(args: {
  periodStart: string;
  periodEnd: string;
  requestIds: string[];
}) {
  return (await api.post('/admin/merchant-payouts/batches', args)).data;
}
export async function listMerchantPayoutBatches(params: { page?: number; pageSize?: number } = {}) {
  return (await api.get<AdminMerchantPayoutBatch[]>('/admin/merchant-payouts/batches', { params }))
    .data;
}
export async function markMerchantPayoutBatchPaid(id: string) {
  return (await api.post(`/admin/merchant-payouts/batches/${id}/mark-paid`)).data;
}
/** Trigger a browser download of a CSV blob fetched with the auth header. */
export function downloadCsv(data: BlobPart, filename: string) {
  downloadBlob(data, filename, 'text/csv');
}
export async function exportMerchantPayoutBatch(id: string) {
  const res = await api.get(`/admin/merchant-payouts/batches/${id}/export`, {
    responseType: 'blob',
  });
  downloadCsv(res.data as BlobPart, `merchant-payout-batch-${id}.csv`);
}
export async function rejectMerchantPayoutBatch(id: string, rejectionReason?: string) {
  return (await api.post(`/admin/merchant-payouts/batches/${id}/reject`, { rejectionReason })).data;
}
